import { Building07 } from "@untitledui/icons";
import { useLoaderData, useLocation } from "react-router";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { Table, TableCard } from "@/components/application/table/table";
import { Pager, SearchBox } from "@/components/admin/list-tools";
import { Deferred } from "@/components/admin/loading";
import { formatDate, type Page, type WorkspaceSummary } from "../lib/api.js";
import { PageHeader } from "./shell.jsx";
import { ListSkeleton } from "./users.jsx";

const COLUMNS = ["名称", "标识", "成员", "创建时间"];

export function WorkspacesSkeleton() {
  return (
    <>
      <PageHeader title="工作区" />
      <SearchBox label="按名称或标识搜索工作区" />
      <ListSkeleton columns={COLUMNS} />
    </>
  );
}

export function WorkspacesRoute() {
  const { data } = useLoaderData<{ data: Promise<Page<WorkspaceSummary>> }>();
  // Keyed by the query, so a search or another page shows as loading instead of holding the old rows.
  const { search } = useLocation();
  return (
    <>
      <PageHeader title="工作区" />
      <SearchBox label="按名称或标识搜索工作区" />
      <Deferred key={search} data={data} fallback={<ListSkeleton columns={COLUMNS} />}>
        {(page) => <Workspaces data={page} />}
      </Deferred>
    </>
  );
}

function Workspaces({ data }: { data: Page<WorkspaceSummary> }) {
  return (
    <>
      {data.items.length === 0 ? (
        <div className="rounded-xl bg-primary py-16 shadow-xs ring-1 ring-secondary">
          <EmptyState size="md">
            <EmptyState.Header pattern="none">
              <EmptyState.FeaturedIcon icon={Building07} color="gray" theme="modern" />
            </EmptyState.Header>
            <EmptyState.Content>
              <EmptyState.Title>没有匹配的工作区</EmptyState.Title>
            </EmptyState.Content>
          </EmptyState>
        </div>
      ) : (
        <TableCard.Root>
          <Table aria-label="工作区">
            <Table.Header>
              <Table.Head id="name" label="名称" isRowHeader />
              <Table.Head id="slug" label="标识" />
              <Table.Head id="members" label="成员" />
              <Table.Head id="createdAt" label="创建时间" />
            </Table.Header>
            <Table.Body items={data.items}>
              {(w) => (
                <Table.Row id={w.id} href={`/workspaces/${w.id}`}>
                  <Table.Cell className="font-medium text-primary">{w.name}</Table.Cell>
                  <Table.Cell>
                    <code className="font-mono text-sm">{w.slug}</code>
                  </Table.Cell>
                  <Table.Cell>{w.members}</Table.Cell>
                  <Table.Cell>{formatDate(w.createdAt)}</Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </TableCard.Root>
      )}
      <Pager total={data.total} offset={data.offset} limit={data.limit} />
    </>
  );
}
